import React, { useContext } from 'react';
import styled, { css, ThemeContext } from 'styled-components';

import Controller from '../Controller/Controller';
import resets from '../../../assets/styled/resets';
import Icon from '../../Icon/Icon';
import Option from './components/Option/Option';
import Group from './components/Group/Group';

import CoreSelectContext from './CoreSelectContext';

import { NewSelectProps } from '../../NewSelect/NewSelect';
import useInkAnalytics from '../../../hooks/useInkAnalytics';
import ErrorContext from '../Controller/errorContext';

import { controllerTokens } from '../../../tokens/components/Controller/tokens';

export const arrowWidth = '28px';

const SelectArrow = styled.div`
  display: inline-flex;
  pointer-events: none;
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%) translateX(-8px);
`;

export const SelectElement = styled.select`
  /* Disable default select styling */
  ${resets.BrowserSelect}

  ${Controller.styles.base};
  ${Controller.styles.textEntry};

  border-radius: ${Controller.styles.borderRadius};
  cursor: pointer;
  padding-right: ${arrowWidth};

  ${props =>
    props.disabled &&
    css`
      cursor: not-allowed;

      ~ ${SelectArrow} {
        color: ${controllerTokens.ContainerFontColorDisabledDefault};
      }
    `}

  /* Using rtl direction because right aligned select is unsupported by Safari */
  ${({ theme }) =>
    theme.controllerAlign === 'right' &&
    css`
      direction: rtl;
      /* Reserves left padding for arrow icon, and resets right padding */
      padding: 10px;
      padding-left: ${arrowWidth};

      /* Flips arrow icon to the left */
      ~ ${SelectArrow} {
        left: 0;
        transform: translateY(-50%) translateX(8px);
      }
    `};
`;

function CoreSelect(props: NewSelectProps) {
  const { externalRef, 'aria-label': ariaLabel, 'data-testid': dataTestId, ...otherProps } = props;
  const [ref] = useInkAnalytics(props, { ref: externalRef });
  const themeContext = useContext(ThemeContext);
  const errorContext = useContext(ErrorContext);

  return (
    <CoreSelectContext.Provider value={props.value}>
      <Controller
        {...otherProps}
        type="select"
        data-testid={dataTestId}
        aria-label={ariaLabel}
        render={controllerProps => {
          const { render, className, style, ...moreProps } = controllerProps;
          return (
            <Controller.Wrapper className={className} theme={{ ...themeContext, ...controllerProps.theme }}>
              {/* Placeholder ' ' fallback adds a character so Safari can do baseline alignment */}
              <SelectElement
                {...moreProps}
                ref={ref}
                placeholder={props.placeholder || ' '}
                aria-errormessage={
                  errorContext.hasError && errorContext.errorAutoInvalidate ? errorContext.errorMessageId : undefined
                }
                aria-invalid={controllerProps.invalid || (errorContext.hasError && errorContext.errorAutoInvalidate)}
              />
              <SelectArrow>
                <Icon type="down" meaning="select option" size="lg" />
              </SelectArrow>
            </Controller.Wrapper>
          );
        }}
      />
    </CoreSelectContext.Provider>
  );
}

CoreSelect.Option = Option;
CoreSelect.Group = Group;

CoreSelect.defaultProps = {
  width: 'large',
  invalid: false,
  disabled: false,
};

export default CoreSelect;
